import React from 'react';
import styled from 'styled-components';
import {
  faUser,
  faRobot,
  faWandMagicSparkles,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { getTheme } from '../../constants/theme';
import {
  manuscriptLabel,
  feedbackLabel,
  latestDisclosure,
} from './aiDisclosure';

// Follow the application's bright/dark palette, including isolated feedback views.
const palette = ({ theme }) =>
  theme.navigation || getTheme('bright').navigation;

const DisclosurePill = styled.span`
  display: inline-block;
  max-width: 100%;
  box-sizing: border-box;
  padding: 3px 10px;
  border: 1px ${({ $undeclared }) => ($undeclared ? 'dashed' : 'solid')}
    ${props => palette(props).border};
  border-radius: 999px;
  background: ${props =>
    props.$generated
      ? palette(props).activeBackground
      : palette(props).surface};
  color: ${props =>
    props.$generated ? palette(props).activeText : palette(props).mutedText};
  font-family: inherit;
  font-size: 0.857rem;
  font-weight: 600;
  line-height: 1.5;
  vertical-align: middle;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const DisclosureRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const DisclosureDetails = styled.div`
  margin-top: 8px;
`;

export function ManuscriptDisclosure({ book, details = false }) {
  const value = latestDisclosure(book);
  return (
    <div style={{ margin: '8px 0' }}>
      <DisclosureRow>
        <DisclosurePill
          $generated={value?.type === 'generated'}
          $undeclared={
            !['human', 'assisted', 'generated'].includes(value?.type)
          }
        >
          {manuscriptLabel(value?.type)}
          {value?.type === 'generated' && (
            <>
              {' '}
              · {value.extent === 'most' ? 'Most or all text' : 'Some passages'}
            </>
          )}
        </DisclosurePill>
        {value?.cover === 'generated' && (
          <DisclosurePill $generated>AI-generated cover</DisclosurePill>
        )}
        {value?.translation === 'generated' && (
          <DisclosurePill $generated>AI translation</DisclosurePill>
        )}
      </DisclosureRow>
      {details && (
        <DisclosureDetails>
          <p>{value?.note}</p>
          <small>
            Declared by the author; not independently verified.{' '}
            <a
              href="https://betareader.io/community-guidelines"
              target="_blank"
              rel="noopener noreferrer"
            >
              AI disclosure rules
            </a>
          </small>
          <p>
            Reader feedback must be the reader's own, not written or generated
            by AI. Readers may not send manuscript text to AI services.
          </p>
        </DisclosureDetails>
      )}
    </div>
  );
}

export function FeedbackBadge({ origin }) {
  return (
    <DisclosurePill
      $generated={origin === 'generated'}
      $undeclared={!['personal', 'assisted', 'generated'].includes(origin)}
    >
      {feedbackLabel(origin)}
    </DisclosurePill>
  );
}

const feedbackIcon = origin =>
  ({ personal: faUser, generated: faRobot, assisted: faWandMagicSparkles })[
    origin
  ] || faCircleQuestion;

function FeedbackSourceIcon({ origin }) {
  const [width, height, , , path] = feedbackIcon(origin).icon;
  return (
    <svg
      width="14"
      height="14"
      viewBox={`0 0 ${width} ${height}`}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={path} />
    </svg>
  );
}

// The whole pill opens the actions; keep the provenance and its menu together.
const FeedbackDetails = styled.details`
  position: relative;
  max-width: 100%;
  margin-left: auto;
  color: ${props => palette(props).text};
  summary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    list-style: none;
    cursor: pointer;
  }
  summary::-webkit-details-marker { display: none; }
  summary:focus-visible {
    outline: 2px solid ${props => palette(props).accent};
    outline-offset: 2px;
  }
  .feedback-source-icon { display: none; }
  &[data-compact='true'] summary {
    min-height: 32px;
    min-width: 56px;
    justify-content: center;
  }
  &[data-compact='true'] .feedback-source-label { display: none; }
  &[data-compact='true'] .feedback-source-icon { display: inline-flex; }
  .br-feedback-menu__panel {
    position: absolute;
    z-index: 30;
    top: 100%;
    right: 0;
    display: grid;
    min-width: 12rem;
    max-width: calc(100vw - 32px);
    padding: 0.35rem;
    border: 1px solid ${props => palette(props).border};
    border-radius: 8px;
    background: ${props => palette(props).surface};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  }
  &:not([open]) .br-feedback-menu__panel { display: none; }
  &[data-direction='right'] .br-feedback-menu__panel {
    right: auto;
    left: 0;
  }
  .br-feedback-menu__panel button,
  .br-feedback-menu__panel a {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 0.6rem 0.75rem;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
  }
  .br-feedback-menu__panel button:hover,
  .br-feedback-menu__panel a:hover {
    background: ${props => palette(props).activeBackground};
  }
  .br-feedback-menu__panel button:focus-visible,
  .br-feedback-menu__panel a:focus-visible {
    outline: 2px solid ${props => palette(props).accent};
  }
`;

function useDisclosureDetails() {
  const detailsRef = React.useRef(null);
  React.useEffect(() => {
    const closeOutside = event => {
      if (detailsRef.current?.open && !detailsRef.current.contains(event.target)) {
        detailsRef.current.open = false;
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, []);
  const onKeyDown = event => {
    if (event.key === 'Escape' && detailsRef.current?.open) {
      event.preventDefault();
      detailsRef.current.open = false;
      detailsRef.current.querySelector('summary')?.focus();
    }
  };
  return {detailsRef, onKeyDown};
}

export function FeedbackMenu({
  origin,
  label,
  compact = false,
  direction = 'left',
  children,
}) {
  const {detailsRef, onKeyDown} = useDisclosureDetails();
  const closeAfterAction = event => {
    if (event.target.closest('button, a')) detailsRef.current.open = false;
  };
  return (
    <FeedbackDetails ref={detailsRef} onKeyDown={onKeyDown} data-compact={compact}
      data-direction={direction}>
      <summary aria-label={`${label}: ${feedbackLabel(origin)}`}
        title={feedbackLabel(origin)}>
        <DisclosurePill
          $generated={origin === 'generated'}
          $undeclared={!['personal', 'assisted', 'generated'].includes(origin)}
        >
          <span className="feedback-source-icon" aria-hidden="true">
            <FeedbackSourceIcon origin={origin} />
          </span>
          <span className="feedback-source-label">{feedbackLabel(origin)}</span>
          <span aria-hidden="true" style={{ fontSize: '16px', lineHeight: 1 }}>
            ⋯
          </span>
        </DisclosurePill>
      </summary>
      <div className="br-feedback-menu__panel br-text" onClick={closeAfterAction}>
        {compact && <span>{feedbackLabel(origin)}</span>}
        {children}
      </div>
    </FeedbackDetails>
  );
}

const ConfirmationBox = styled.div`
  margin: 12px 0;
  padding: 14px 16px;
  border: 1px solid ${props => palette(props).border};
  border-radius: 12px;
  background: ${props => palette(props).surface};
  color: ${props => palette(props).text};
  label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: inherit;
    line-height: 1.5;
  }
  input[type='checkbox'] {
    flex: 0 0 auto;
    margin-top: 4px;
    accent-color: var(--br-accent);
  }
  input[type='checkbox']:focus-visible {
    outline: 2px solid var(--br-focus);
    outline-offset: 2px;
  }
  small {
    display: block;
    line-height: 1.5;
  }
  p {
    margin: 8px 0 0;
    font-size: 0.85em;
  }
`;

export function FeedbackOrigin({ value, onChange }) {
  const confirmation =
    'I confirm this is my own feedback, based on my reading, and I did not use AI to process the manuscript or write this feedback.';
  return (
    <ConfirmationBox>
      <label>
        <input type="checkbox" checked={value === 'personal'}
          onChange={event => onChange(event.target.checked ? 'personal' : 'undeclared')} />
        <span>{confirmation}</span>
      </label>
      <small>
        Write your own observations in your own words. Do not use AI to analyze
        the manuscript or draft or rewrite your feedback.
      </small>
      <p>Do not send manuscript text to AI services.</p>
    </ConfirmationBox>
  );
}

function ReportAiDisclosureMenu({feedbackId, item}) {
  const {detailsRef, onKeyDown} = useDisclosureDetails();
  return <FeedbackDetails ref={detailsRef} onKeyDown={onKeyDown}
    data-direction="left" style={{float: 'right'}}>
    <summary aria-label={feedbackId ? 'Feedback actions' : 'Manuscript actions'}>
      ⋯
    </summary>
    <div className="br-feedback-menu__panel br-text">{item}</div>
  </FeedbackDetails>;
}

export function ReportAiDisclosure({ bookId, feedbackId, menuItem = false }) {
  const subject = encodeURIComponent('Report misleading AI disclosure');
  const body = encodeURIComponent(
    `Book: https://app.betareader.io/books/${bookId}\n${feedbackId ? `Feedback: ${feedbackId}\n` : ''}Please describe the disclosure concern and supporting context:\n`
  );
  const item = <a href={`mailto:hello@betareader.io?subject=${subject}&body=${body}`}>
    Report misleading AI disclosure
  </a>;
  if (menuItem) return item;
  return <ReportAiDisclosureMenu feedbackId={feedbackId} item={item} />;
}
